/**
 * BusinessLocationPage — /businesses/in/:location
 *
 * SEO-friendly landing page listing every business in a given city,
 * state, or country. Slugs are lower-kebab (e.g. `san-francisco`,
 * `india`, `california`) — the backend fuzz-matches against city /
 * state / country columns.
 *
 * Data source: ``GET /api/businesses/by-location/{location}``.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Building2, MapPin, Loader2, ArrowLeft, Search } from "lucide-react";

function prettify(slug) {
  return (slug || "")
    .replace(/-/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export default function BusinessLocationPage() {
  const { location } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [industryFilter, setIndustryFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/businesses/by-location/${encodeURIComponent(location)}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load businesses for this location.");
      setData({ items: [], total: 0, location: prettify(location), top_industries: [] });
    } finally { setLoading(false); }
  }, [location]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Set the browser tab title for a nicer SEO snapshot when shared.
    const name = data?.location || prettify(location);
    document.title = `Businesses in ${name} — Social Pulse`;
    return () => { document.title = "Social Pulse"; };
  }, [data, location]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" />
      </div>
    );
  }

  const filtered = industryFilter
    ? (data.items || []).filter((b) => (b.industry || "") === industryFilter)
    : (data.items || []);

  const name = data?.location || prettify(location);
  const empty = (data.total || 0) === 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="business-location-page">
      <Link to="/businesses" className="text-xs opacity-70 inline-flex items-center gap-1 mb-3" data-testid="location-back">
        <ArrowLeft className="w-3 h-3" /> All businesses
      </Link>

      <div className="mb-8">
        <div className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider opacity-70 mb-2">
          <MapPin className="w-3.5 h-3.5" /> {data.kind || "location"}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Businesses in {name}</h1>
        <p className="mt-2 opacity-80" data-testid="location-total">
          {empty
            ? `No businesses have listed themselves in ${name} yet.`
            : `${data.total} business${data.total === 1 ? "" : "es"} across ${data.top_industries?.length || 0} categor${(data.top_industries?.length || 0) === 1 ? "y" : "ies"}.`}
        </p>
      </div>

      {data.top_industries?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="location-industry-chips">
          <button
            type="button"
            onClick={() => setIndustryFilter("")}
            className="text-xs px-3 py-1 rounded-full border"
            style={{
              background: industryFilter === "" ? "var(--wofy-primary)" : "transparent",
              color:      industryFilter === "" ? "white" : "var(--wofy-text)",
              borderColor: "var(--wofy-border)",
            }}
            data-testid="chip-all"
          >
            All ({data.total})
          </button>
          {data.top_industries.map(({ id, count }) => (
            <button
              key={id}
              type="button"
              onClick={() => setIndustryFilter(id)}
              className="text-xs px-3 py-1 rounded-full border capitalize"
              style={{
                background: industryFilter === id ? "var(--wofy-primary)" : "transparent",
                color:      industryFilter === id ? "white" : "var(--wofy-text)",
                borderColor: "var(--wofy-border)",
              }}
              data-testid={`chip-industry-${id}`}
            >
              {id} ({count})
            </button>
          ))}
        </div>
      )}

      {empty ? (
        <div className="wofy-card !p-10 text-center max-w-lg mx-auto" data-testid="location-empty">
          <Building2 className="w-10 h-10 mx-auto opacity-40 mb-2" />
          <p className="font-display text-xl mb-1">Be the first to list in {name}</p>
          <p className="text-sm opacity-70 mb-4">Add your business, restaurant, or service — get discovered by locals.</p>
          <Link to="/businesses" className="wofy-btn-primary inline-flex items-center gap-1.5">
            <Search className="w-4 h-4" /> Explore the Portal
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="location-results">
          {filtered.map((biz) => (
            <Link
              key={biz.id}
              to={`/business/${biz.slug}`}
              className="wofy-card !p-4 hover:shadow-md transition-shadow"
              data-testid={`location-biz-${biz.slug}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center text-white font-display text-xl"
                  style={{ background: biz.logo_url ? undefined : "var(--wofy-primary)" }}
                >
                  {biz.logo_url
                    ? <img src={biz.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    : (biz.name || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg leading-tight truncate">{biz.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mt-0.5">
                    {biz.industry || "business"} · {[biz.city, biz.country].filter(Boolean).join(", ")}
                  </div>
                  {biz.description && (
                    <p className="text-xs opacity-80 mt-1.5 line-clamp-2">{biz.description}</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
